import { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useSelector, useDispatch } from '../../../redux/store';
import { getPreviousVendorOrders } from '../../../redux/slices/orders';
import { COMPLETED_ORDERS, COMPLETED_ORDERS_BY_VENDOR } from '../../../reusable-components/datagrid/orderColumns';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import CustomToolbar from './CustomToolbar';
import OrderNotesViewModal from './OrderNotesViewModal';
import { useTabs } from '../../../context/TabContext';
import ManagerChipsWrapper from '../../../reusable-components/datagrid/ManagerChipsWrapper';

export default function CompletedOrdersView({ viewByVendor }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('start');
  const endDate = searchParams.get('end');
  const [showSelection, setShowSelection] = useState('');
  const { addNewTabAndNavigate } = useTabs();

  const orders = useSelector((state) => state.orders);
  const {
    data: { previousOrders },
  } = orders;
  const vendorOrders = orders?.vendorOrders;
  const isLoading = orders?.isLoading;
  const [activeRows, setActiveRows] = useState([]);

  const data = useMemo(
    () => (viewByVendor ? (vendorOrders ?? []) : (previousOrders ?? [])),
    [viewByVendor, vendorOrders, previousOrders]
  );

  useEffect(() => {
    if (!viewByVendor) return;
    dispatch(getPreviousVendorOrders({ startDate, endDate }));
  }, [dispatch, viewByVendor, startDate, endDate]);

  const customToolbar = useCallback(() => {
    const isFiltered = (filter) => filter === searchParams.get('filter');
    return (
      <CustomToolbar
        setShowSelection={setShowSelection}
        showSelection={showSelection}
        isFiltered={isFiltered}
        activeRows={activeRows}
      />
    );
  }, [searchParams, showSelection, activeRows]);

  const messageCount = {
    field: 'messageCount',
    headerName: 'Notes',
    minWidth: 60,
    flex: 0.5,
    renderCell: (params) => <OrderNotesViewModal order={params.row} />,
  };

  const manager = ManagerChipsWrapper(null, 'Orders');
  const columns = [...(viewByVendor ? COMPLETED_ORDERS_BY_VENDOR : COMPLETED_ORDERS), manager, messageCount];

  const rowClick = (row) => {
    const params = new URLSearchParams({ filter: searchParams.get('filter') ?? '', start: startDate ?? '', end: endDate ?? '' });
    if (viewByVendor) params.set('vendorId', row.vendorId);
    navigate(`/dashboard/orders/${row.id}?${params.toString()}`);
  };

  return (
    <>
      <Grid item xs={12}>
        <CustomDataGrid
          key={viewByVendor ? 'completed-orders-by-vendor-view' : 'completed-orders-view'}
          gridId={viewByVendor ? 'completed-orders-by-vendor-view' : 'completed-orders-view'}
          boxSX={{ height: 'calc(100vh - 240px)' }}
          applyQuickFilter
          data={data}
          gridColumns={columns}
          quickfilterHiddenSearchCol={{
            field: 'orderMessages',
            valueGetter: (params) => [
              ...(params.row.orderMessages ?? []),
              ...(params.row.deleteFlag ? ['Deleted', params.row.deletedBy, params.row.deletedReason] : []),
            ].filter(Boolean),
          }}
          defaultHiddenColumns={['department', 'submittedBy', 'orderDelay', 'dateApproved','reviewers', 'itemCount', 'total']}
          sort={[{ field: 'dateApproved', sort: 'desc' }]}
          getRowId={(row) => (viewByVendor ? `${row.id}-${row.vendorId}` : row.id)}
          onRowClick={(params, e) => {
            const selection = window.getSelection().toString();
            if (selection.length > 0) return;
            if (e.deleteChip || e.addChip || e.openTicket || e.combine || e.confirmDialog) return;
            if (e.navigateToTickets) {
              addNewTabAndNavigate(`/dashboard/tickets/${e.navigateToTickets}`);
              return;
            }
            rowClick(params.row);
          }}
          disableRowSelectionOnClick
          CustomLeftToolbar={customToolbar}
          isLoading={isLoading}
          setActiveRows={setActiveRows}
          advancedFilter
          advancedSearchBase={viewByVendor ? 'vendorOrders' : 'previousOrders'}
        />
      </Grid>
    </>
  );
}
